/*
 eslint-disable react/jsx-one-expression-per-line, react/jsx-curly-newline, react/no-array-index-key
 */
import React, {
  useState,
  useEffect,
  lazy,
  Suspense,
  useCallback,
  useRef,
  memo,
} from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Draggable Scroll
import { useDraggable } from 'react-use-draggable-scroll';

// React Virtuoso
import { TableVirtuoso } from 'react-virtuoso/dist/index';

// Actions
import { setSecondOpen } from 'store/ducks/dialogs/actions';

// Material
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TableRow from '@material-ui/core/TableRow';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Grid from '@material-ui/core/Grid';

// Lodash

// Components
import { PrettiNameLine } from 'components/ReportComponents';

// Types
import { ApplicationState } from 'store';
import { EntrySimplifiedInterface } from 'store/ducks/reports/types';

// Helpers
import { returnDateAndTime } from 'helpers/date';
import { Order, stableSort, getComparator } from 'helpers/tableSortLabels';

// Contants
import BALANCE from 'constants/balance';
import CONST_QRCODE from 'constants/qrcodes';
import PATH from 'constants/basePath';
import IMAGES from 'constants/images';

// Styles
import useStyles from '../../chartReport.styles';

// Lazy Components
const DetailsDialog = lazy(() => import('./components/DetailsDialog'));

interface TableChartInterface {
  isHistoryDatails?: boolean;
  entrysList?: EntrySimplifiedInterface[];
  isPrinting?: boolean;
  isMounted?: 'initalState' | 'loading' | 'notLoading';
  setIsMounted?: (param: 'initalState' | 'loading' | 'notLoading') => void;
}

const TableChartComponent: React.FC<TableChartInterface> = ({
  isHistoryDatails,
  entrysList = [],
  isPrinting,
  isMounted,
  setIsMounted,
}) => {
  const classes = useStyles();
  const parentRef: any = useRef();
  const { events } = useDraggable(parentRef);
  const dispatch = useDispatch();

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('usedAt');
  const [grouping, setGrouping] = useState<any[]>([]);

  const chartEntryReportSimplifiedList = useSelector(
    (state: ApplicationState) =>
      state.reports.chartEntryReportSimplifiedList || entrysList,
  );

  const entryReportListGrouped = useSelector(
    (state: ApplicationState) => state.reports.entryReportListGrouped,
  );

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  useEffect(() => {
    if (entryReportListGrouped) {
      setGrouping(
        entryReportListGrouped
          .sort(
            (a: any, b: any) =>
              Math.abs(new Date(a.usedAt).getTime()) -
              Math.abs(new Date(b.usedAt).getTime()),
          )
          .map((item, index: number) => ({
            ...item,
            position: index + 1,
          })),
      );
    }
  }, [entryReportListGrouped]);

  const handleRequestSort = useCallback(
    (event: React.MouseEvent<unknown>, property: string) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy],
  );

  const formatQrcodeType = (qrcodeType: number) =>
    CONST_QRCODE.ENTRY.find((e) => e.KEY === qrcodeType)?.VALUE || '--';

  return (
    <Grid>
      <TableVirtuoso
        style={{
          height: '90vh',
          minHeight: '80px',
          overflowY: 'hidden',
        }}
        overscan={10}
        useWindowScroll
        data={stableSort(
          chartEntryReportSimplifiedList,
          getComparator(order, orderBy),
        )}
        components={{
          Scroller: React.forwardRef((props, ref) => (
            <TableContainer
              className={classes.table}
              {...props}
              ref={parentRef}
              {...events}
            />
          )),

          Table: (props: any) => (
            <Table {...props} style={{ borderCollapse: 'separate' }} />
          ),
          TableHead,
          TableRow,
          TableBody: React.forwardRef((props, ref) => (
            <TableBody {...props} ref={ref} />
          )),
        }}
        fixedHeaderContent={() => (
          <TableRow>
            <TableCell
              variant='head'
              align='left'
              className={isHistoryDatails ? classes.firstTd : ''}
              sortDirection={orderBy === 'id' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'id' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'id')}>
                ID
                {orderBy === 'id' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'line' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'line' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'line')}>
                Linha
                {orderBy === 'line' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'benefit' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'benefit' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'benefit')}>
                Benefício
                {orderBy === 'benefit' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'createdAt' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'createdAt' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'createdAt')}>
                Data
                {orderBy === 'createdAt' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'value' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'value' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'value')}>
                Valor
                {orderBy === 'value' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'usageType' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'usageType' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'usageType')}>
                Tipo
                {orderBy === 'usageType' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'qrcodeType' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'qrcodeType' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'qrcodeType')}>
                QRCode
                {orderBy === 'qrcodeType' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'id' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'id' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'id')}>
                Identificador
                {orderBy === 'id' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'lat' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'lat' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'lat')}>
                Latitude
                {orderBy === 'lat' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'lng' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'lng' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'lng')}>
                Longitude
                {orderBy === 'lng' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'dbd' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'dbd' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'dbd')}>
                DBD
                {orderBy === 'dbd' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'place' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'place' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'place')}>
                Local
                {orderBy === 'place' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'userId' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'userId' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'userId')}>
                ID Usuário
                {orderBy === 'userId' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'userName' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'userName' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'userName')}>
                Nome Usuário
                {orderBy === 'userName' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            {transportOperatorSelected.type === 1 && (
              <TableCell
                variant='head'
                align='left'
                sortDirection={
                  orderBy === 'transportOperatorId' ? order : false
                }>
                <TableSortLabel
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}
                  active
                  direction={orderBy === 'transportOperatorId' ? order : 'asc'}
                  onClick={(event) =>
                    handleRequestSort(event, 'transportOperatorId')
                  }>
                  Operação
                  {orderBy === 'transportOperatorId' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            )}
            <TableCell variant='head' />
          </TableRow>
        )}
        itemContent={(index: any, data: any) => (
          <>
            <TableCell>{data.id ? data.id.substring(0, 6) : ''}</TableCell>
            <TableCell>
              <PrettiNameLine
                line={data.line}
                color={data?.color || '#9427EA'}
              />
            </TableCell>
            <TableCell>{data.benefit ? data.benefit : ''}</TableCell>
            <TableCell>
              {data.createdAt ? returnDateAndTime(data.createdAt) : ''}
            </TableCell>
            <TableCell>
              {`R$ ${data.value.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
              })}`}
            </TableCell>
            <TableCell>
              {BALANCE.USAGE_TYPES.find(({ ID }) => ID === data.usageType)
                ?.VALUE || '--'}
            </TableCell>
            <TableCell align='center'>
              {formatQrcodeType(data?.qrcodeType)}
            </TableCell>
            <TableCell align='center'>{data?.description || '--'}</TableCell>
            <TableCell>{data?.lat || '--'}</TableCell>
            <TableCell>{data?.lng || '--'}</TableCell>
            <TableCell>{data?.dbd || '--'}</TableCell>
            <TableCell>{data?.place || '--'}</TableCell>
            <TableCell>
              <a
                href={`${PATH.PASSENGER.BASE_URL}/editar/${
                  data?.userId ? data?.userId : ''
                }`}
                target='_blank'
                rel='noreferrer'
                className={classes.link}>
                {data?.userId ? data?.userId.substring(0, 6) : '--'}
              </a>
            </TableCell>
            <TableCell>{data?.userName || '--'}</TableCell>
            {transportOperatorSelected.type === 1 && (
              <TableCell>
                {transportOperatorsList.find(
                  (e) => e.id === data?.transportOperatorId,
                )?.name || '---'}
              </TableCell>
            )}
            <TableCell>
              <Tooltip placement='top' title='Detalhes'>
                <IconButton
                  onClick={() => {
                    dispatch(
                      setSecondOpen({
                        open: true,
                        maxWidth: 'xl',
                        content: (
                          <Suspense fallback='Carregando...'>
                            <DetailsDialog
                              balance={data}
                              entryId={data?.id || ''}
                            />
                          </Suspense>
                        ),
                      }),
                    );
                  }}>
                  <img
                    style={{ width: 32 }}
                    src={IMAGES.ICONS.DETAILS}
                    alt='DETAILS'
                  />
                </IconButton>
              </Tooltip>
            </TableCell>
          </>
        )}
      />
    </Grid>
  );
};

const TableChart = memo(TableChartComponent);

export { TableChart };
export default TableChart;
